import { Fragment, useState, useContext, useEffect } from 'react';
import Icons from './common/Icons';
import ProtectedComponent from './common/ProtectedComponent';
import { addMaterial, updateMaterial, deleteMaterial, materialTypeOptions, materialSourceDisplay, materialSourceOptions } from '../services/materialService';
import { AuthContext } from './Login/AuthContext';
import { Button } from 'react-bootstrap'
import DatabaseContext from './DatabaseContext';

import TableViewAdd, { defaultAdd, defaultResetAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultReset, defaultEdit, defaultValidateEdit } from "./common/TableViewEdit"
import { HGCFormRowText, HGCFormRowSelect, HGCFormRowBool } from './common/SimpleForm';
import { boolToIcon, noCheck, validateAlphanumeric, validateDecimal } from '../services/commonParse';
import PaginateTable, { BooleanFilter, CategoryFilter, FuzzyTextFilter } from './common/TableDisplay';
import { findTableRow } from '../services/tableUtils';

const MaterialColumns = () => [
  { Header: 'Batch ID', Cell: MaterialIDCell, Filter: FuzzyTextFilter((mat) => [mat.batch_id]) },
  { Header: 'Manufacturer', Cell: (mat) => materialSourceDisplay(mat.source), Filter: CategoryFilter((mat) => mat.source) },
  { Header: 'Type', Cell: (mat) => mat.type, Filter: CategoryFilter((mat) => mat.type) },
  { Header: 'Cu % composition', Cell: (mat) => mat.cu_composition },
  { Header: 'in stock', Cell: (mat) => boolToIcon(mat.in_stock), Filter: BooleanFilter((mat) => mat.in_stock, "true") }
]

const MaterialIDCell = (mat) => {
  const [showEdit, setShowEdit] = useState(false);
  const { user } = useContext(AuthContext);
  const { updateMaterialList } = useContext(DatabaseContext);
  const removeAction = async () => { await deleteMaterial(mat.id); updateMaterialList(); }

  return <Fragment>
    {mat.batch_id} ({mat.shorthand})
    < Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}> {Icons.edit}</Button >
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    {showEdit && <MaterialEdit id={mat.id} showState={[showEdit, setShowEdit]} />}
  </Fragment >
}

const MaterialEdit = ({ id, showState }) => {
  const [, setShow] = showState;
  const { materialList, updateMaterialList } = useContext(DatabaseContext);
  const origData = findTableRow(materialList, (x) => x.id === id)
  // Creating all items related to the display form
  const form = {
    "batch_id": useState(origData.batch_id),
    'source': useState(origData.source),
    "shorthand": useState(origData.shorthand),
    "type": useState(origData.type),
    "cu_composition": useState(origData.cu_composition),
    "in_stock": useState(origData.in_stock)
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    "source": useState(""),
    "shorthand": useState(""),
    "type": useState(""),
    "cu_composition": useState(""),
  }
  const [invalid, setInvalid] = useState("");

  const resetAction = defaultReset(form, origData)
  const editAction = defaultEdit(id, form, updateMaterial, updateMaterialList, setShow)
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewEdit title={`Edit material [${id}][${origData ? origData.batch_id : ''}]`} showState={showState} resetAction={resetAction} editAction={editAction} invalid={invalid}>
    <HGCFormRowText label="Batch id" dataState={form["batch_id"]} errorState={errorForm["batch_id"]} validate={validateAlphanumeric(true)} />
    <HGCFormRowSelect label="Manufacturer" dataState={form["source"]} errorState={errorForm["source"]} options={materialSourceOptions} allowNull={false} />
    <HGCFormRowText label="Short-hand" dataState={form["shorthand"]} errorState={errorForm["shorthand"]} validate={noCheck} />
    <HGCFormRowSelect label="Material type" dataState={form["type"]} errorState={errorForm["type"]} options={materialTypeOptions} allowNull={false} />
    <HGCFormRowText label="Cu Composition [%]" dataState={form["cu_composition"]} errorState={errorForm["cu_composition"]} validate={validateDecimal()} />
    <HGCFormRowBool label="In stock" dataState={form["in_stock"]} />
  </TableViewEdit>
}

const MaterialAdd = ({ showState }) => {
  const { updateMaterialList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const form = {
    'batch_id': useState(null),
    "source": useState(null),
    'shorthand': useState(null),
    'type': useState(null),
    'cu_composition': useState(null),
    'in_stock': useState(true),
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    "source": useState(""),
    "shorthand": useState(""),
    "type": useState(""),
    "cu_composition": useState("")
  };

  const [invalid, setInvalid] = useState("");
  const resetAction = defaultResetAdd(form, {})
  const addAction = defaultAdd(form, addMaterial, updateMaterialList, resetAction, setShow);
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewAdd title="Add new Material" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="Batch id" dataState={form["batch_id"]} errorState={errorForm["batch_id"]} validate={validateAlphanumeric(true)} autoFocus={true} />
    <HGCFormRowSelect label="Manufacturer" dataState={form["source"]} errorState={errorForm["source"]} options={materialSourceOptions} allowNull={false} />
    <HGCFormRowText label="Short-hand" dataState={form["shorthand"]} errorState={errorForm["shorthand"]} validate={noCheck} />
    <HGCFormRowSelect label="Material type" dataState={form["type"]} errorState={errorForm["type"]} options={materialTypeOptions} allowNull={false} />
    <HGCFormRowText label="Cu Composition [%]" dataState={form["cu_composition"]} errorState={errorForm["cu_composition"]} validate={validateDecimal()} />
    <HGCFormRowBool label="In stock" dataState={form["in_stock"]} />
  </TableViewAdd>
}

const MaterialList = () => {
  const { materialList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>Add new material</Button>
    <MaterialAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={materialList} columnLayout={MaterialColumns()} />
  </ProtectedComponent>

}

export default MaterialList;
